import { ENG_DAYS, TZ_OFFSET, DayHour, WeekDayNumber, MonthNumber, DayNumber, MinuteNumber, SecondNumber } from '@iotv/iotv-v3-types'
import { Milliseconds } from '../../constants'

import { comparableProductOf } from './Periodization/TimeBuckets'

export const getEngDay = (stdDayNumber: number) => Object.values(ENG_DAYS)[stdDayNumber]
export const getStdDay = (engDay: ENG_DAYS) => Object.values(ENG_DAYS).findIndex((day) => day === engDay)

export type GetUTCDayHourRequest = { tzOffset: TZ_OFFSET; day: ENG_DAYS; hour: DayHour }
export type GetUTCDayHourResult = GetUTCDayHourRequest

export const getUTCDayHour = ({ tzOffset, day, hour }: GetUTCDayHourRequest): GetUTCDayHourResult => {
  const baseDate = new Date()
  const baseDay: WeekDayNumber = baseDate.getUTCDay() as WeekDayNumber

  baseDate.setUTCDate(baseDate.getUTCDate() + (getStdDay(day) - baseDay))
  baseDate.setUTCHours(Math.round(hour - tzOffset))

  return { tzOffset: 0, day: getEngDay(baseDate.getUTCDay()), hour: baseDate.getUTCHours() as DayHour }
}

export const timestampToYMDHMS = (timestamp: number) => {
  const localDate = new Date(timestamp)
  const [y, m, d, h, min, s] = [
    localDate.getUTCFullYear(),
    localDate.getUTCMonth(),
    localDate.getUTCDate(),
    localDate.getUTCHours(),
    localDate.getUTCMinutes(),
    localDate.getUTCSeconds(),
  ]
  return [y, m, d, h, min, s] as [number, MonthNumber, DayNumber, DayHour, MinuteNumber, SecondNumber]
}

export const daysInLastUTCMonth = (timestamp: number): number => {
  const date = new Date(timestamp)
  date.setUTCDate(0)
  return date.getUTCDate()
}

export const daysInThisUTCMonth = (timestamp: number): number => {
  const date = new Date(timestamp)
  date.setUTCMonth(date.getUTCMonth() + 1, 0)
  return date.getUTCDate()
}

export const daysInNextUTCMonth = (timestamp: number): number => {
  const date = new Date(timestamp)
  date.setUTCMonth(date.getUTCMonth() + 2, 0)
  return date.getUTCDate()
}

export const daysInLastUTCYear = (timestamp: number): number => {
  const current = new Date(timestamp)
  const oneYear = new Date(current)
  oneYear.setUTCFullYear(oneYear.getUTCFullYear() - 1, current.getUTCMonth(), current.getUTCDate())
  const days = (current.valueOf() - oneYear.valueOf()) / Milliseconds.DAY
  return days
}

export const daysInLastUTCDecade = (timestamp: number): number => {
  const current = new Date(timestamp)
  const oneDecade = new Date(current)
  oneDecade.setUTCFullYear(oneDecade.getUTCFullYear() - 10, current.getUTCMonth(), current.getUTCDate())
  const days = (current.valueOf() - oneDecade.valueOf()) / Milliseconds.DAY
  return days
}

export const monthDaysDiff = (accessorMonth: number, currentMonth: number, referenceYearTimestamp: number): number => {
  let daysTotal = 0
  const multiplier = accessorMonth - currentMonth >= 0 ? 1 : -1
  const startMonth = Math.min(accessorMonth, currentMonth)
  const endMonth = Math.max(accessorMonth, currentMonth)
  let evaluatedMonth = startMonth
  while (evaluatedMonth !== endMonth) {
    const monthDate = new Date(referenceYearTimestamp)
    monthDate.setUTCMonth(evaluatedMonth, 1)

    daysTotal += daysInThisUTCMonth(monthDate.valueOf())
    evaluatedMonth++
  }

  return daysTotal * multiplier
}

export const monthDiffYearsDaysDiff = (accessorYear: number, accessorMonth: number, currentYear: number, currentMonth: number): number => {
  let daysTotal = 0
  const rdx = [12, 31]
  const multiplier = comparableProductOf([accessorYear, accessorMonth], rdx) > comparableProductOf([currentYear, currentMonth], rdx) ? 1 : -1
  const [startYear, startMonth] = multiplier === 1 ? [currentYear, currentMonth] : [accessorYear, accessorMonth]
  const [endYear, endMonth] = multiplier === 1 ? [accessorYear, accessorMonth] : [currentYear, currentMonth]
  let evaluatedMonth = new Date()
  evaluatedMonth.setUTCFullYear(startYear, startMonth, 1)
  // console.log({eval: evaluatedMonth.toUTCString(), startYear, startMonth, endYear, endMonth })
  while (!(evaluatedMonth.getUTCFullYear() === endYear && evaluatedMonth.getUTCMonth() === endMonth)) {
    daysTotal += daysInThisUTCMonth(evaluatedMonth.valueOf())
    evaluatedMonth.setUTCMonth(evaluatedMonth.getUTCMonth() + 1)
    // console.log({eval: evaluatedMonth.toUTCString(), startYear, startMonth, endYear, endMonth })
  }

  return daysTotal * multiplier
}

export const yearDayDiff = (accessorYear: number, currentYear: number, referenceYearTimestamp: number): number => {
  let daysTotal = 0
  const multiplier = accessorYear - currentYear >= 0 ? 1 : -1
  const startYear = Math.min(accessorYear, currentYear)
  const endYear = Math.max(accessorYear, currentYear)
  let evaluatedYear = startYear
  while (evaluatedYear !== endYear) {
    const YearDate = new Date(referenceYearTimestamp)
    YearDate.setUTCFullYear(evaluatedYear, 1, 1)

    daysTotal += daysInLastUTCYear(YearDate.valueOf())
    evaluatedYear++
  }

  return daysTotal * multiplier
}
